// Color math for the color toolkit cluster. Pure functions, no dependencies.

export type ColorVariant = 'hex-to-rgb' | 'rgb-to-hex' | 'color-shades-generator' | 'color-mixer';

export type Rgb = { r: number; g: number; b: number };

function clamp(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function parseHex(hex: string): Rgb | null {
  let value = hex.trim().replace(/^#/, '');
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  return (
    '#' +
    [r, g, b]
      .map((channel) => clamp(channel).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
}

export function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  if (delta !== 0) {
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  return { h, s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function formatColorOutputs(rgb: Rgb): {
  hex: string;
  rgb: string;
  rgba: string;
  hsl: string;
} {
  const hsl = rgbToHsl(rgb);
  return {
    hex: rgbToHex(rgb),
    rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
    rgba: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`,
    hsl: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`,
  };
}

// Generate shades (toward black) and tints (toward white) of a base color.
export function generateShades(base: Rgb, steps = 10): string[] {
  const result: string[] = [];
  for (let i = 0; i < steps; i += 1) {
    const factor = i / (steps - 1); // 0 = darkest, 1 = lightest
    // Map factor to a mix between black (0.15) and white (0.85) blended with base.
    const mix = factor * 2 - 1; // -1 dark, +1 light
    const target = mix < 0 ? 0 : 255;
    const amount = Math.abs(mix);
    result.push(
      rgbToHex({
        r: base.r + (target - base.r) * amount,
        g: base.g + (target - base.g) * amount,
        b: base.b + (target - base.b) * amount,
      }),
    );
  }
  return result;
}

export function mixColors(a: Rgb, b: Rgb, ratio: number): Rgb {
  const t = Math.max(0, Math.min(1, ratio));
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}
